import SpineWrapper from '@Core/Animation/SpineWrapper';
import { GameComponent } from '@Core/GameComponent/GameComponent';
import AssetsLoadManager from '@Core/Loader/AssetsLoadManager';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ICollector } from '@GFTypes/GameStoreTypes';
import { ELayeringOrder } from '@GFTypes/LayeringOrder';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { GameScene } from '..';

export class Collector extends GameComponent {
    static readonly SPINE_KEY: string = 'collectors';
    static readonly SKIN_NAME: string = 'default';
    public scene: GameScene;
    private spineWrapper: SpineWrapper | undefined;
    private hasDedicatedSpine: boolean;
    constructor(scene: GameScene) {
        super(scene);
        this.scene = scene;
        Logger.log(LoggerLevel.Constructor, {
            source: LoggerSource.Game,
            message: 'Collectors constructor'
        });

        EventsCenter.on(EGameStates.PRE_SPIN_STATE, this.updateCollectors, this);
        EventsCenter.on(EGameEvents.BET_UPDATE, this.updateCollectors, this);
    }

    private initSpine(): void {
        this.hasDedicatedSpine = AssetsLoadManager.getSpinesConfiguration().assets.some((asset) => asset.key === 'collectors');
        if (this.hasDedicatedSpine) {
            this.spineWrapper = new SpineWrapper(
                this.scene,
                {
                    x: this.scene.screen.width / 2,
                    y: this.scene.screen.height / 2
                },
                { dataKey: Collector.SPINE_KEY, skinName: Collector.SKIN_NAME },
                true
            );
            this.spineWrapper.spine.zIndex = ELayeringOrder.REELS_BACKGROUND;
        } else {
            this.spineWrapper = this.scene.game.spines.find((spine) =>
                spine.eventManager.hasGameEventAnimation(EGameEvents.COLLECTOR_$_LEVEL_$_IN, true, [1, 1])
            );
        }
    }

    public onCreate(): void {
        this.initSpine();
        this.updateActiveCollectors(false);
        this.changeLevel();
    }

    private updateCollectors(): void {
        this.updateActiveCollectors();
        this.changeLevel();
    }

    private updateActiveCollectors(fromSpinData: boolean = true): void {
        const previousCollectors = this.gameStore.get(EGameStoreKeys.Collectors)?.activeCollectors;
        const authData = this.gameStore.get(EGameStoreKeys.AuthenticateGameConfig).collectors;
        const spinData = this.gameStore.get(EGameStoreKeys.SpinOutcome)?.steps?.[0]?.collectors;
        const collectorsData = fromSpinData && spinData ? spinData : authData;
        const currentBet = this.scene.game.gameStore.get(EGameStoreKeys.BetState).value.raw;
        const collectors: ICollector[] = [];
        if (!collectorsData) return;
        const perBetCollectors = collectorsData.collectorsPerBet.size > 0;
        const collectorsPerBet = collectorsData.collectorsPerBet;
        collectors.push(...(perBetCollectors ? collectorsPerBet.get(currentBet)! : collectorsData.collectors));
        const collectorsToChangeLevel = collectors
            .filter((collector) => collector.current !== previousCollectors?.find((prevCollector) => prevCollector.id === collector.id)?.current)
            .map((collector) => collector.id);
        this.gameStore.set(EGameStoreKeys.Collectors, { activeCollectors: collectors, collectorsToChangeLevel });
    }

    private async changeLevel(): Promise<void> {
        const activeCollectors = this.gameStore.get(EGameStoreKeys.Collectors)?.activeCollectors!;
        const collectorsToChangeLevel = this.gameStore.get(EGameStoreKeys.Collectors)?.collectorsToChangeLevel!;

        for (const collector of activeCollectors) {
            if (!collectorsToChangeLevel.includes(collector.id)) return;
            EventsCenter.emit(EGameEvents.COLLECTOR_$_LEVEL_$_IN, { eventIds: [collector.id!, collector.current] });

            await this.spineWrapper!.waitForAnimationEvent(EGameEvents.COLLECTOR_$_LEVEL_$_IN, 'complete', [collector.id!, collector.current]).promise;
            const wasInterrupted = !this.spineWrapper?.eventManager.isCurrentlyPlayingGameEvent(EGameEvents.COLLECTOR_$_LEVEL_$_IN, [
                collector.id!,
                collector.current
            ]);
            if (wasInterrupted) continue;
            EventsCenter.emit(EGameEvents.COLLECTOR_$_LEVEL_$, { eventIds: [collector.id!, collector.current] });
        }
    }
}
